import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { FaGlobe, FaSimCard, FaWifi } from "react-icons/fa";
import "./MobilePlans.css";
import { Link } from "react-router-dom";

export default function MobilePlans(): JSX.Element {
  const [category, setCategory] = useState<"voice" | "data">("voice");
  const [variant, setVariant] = useState<"main" | "share">("main");

  const planData = {
    voice: {
      main: [
        { name: "15GB", price: "$40", data: "15GB Mobile Data", note: "5G Access • 1000GB data bank", popular: false },
        { name: "45GB", price: "$45", data: "45GB Mobile Data", note: "5G Access • 1000GB data bank • Calls to 15 countries*", popular: true },
        { name: "65GB", price: "$50", data: "65GB Mobile Data", note: "5G Access • 1000GB data bank • Calls to 15 countries*", popular: false },
        { name: "100GB", price: "$60", data: "100GB Mobile Data", note: "5G Access • 1000GB data bank • Calls to 15 countries*", popular: false },
        { name: "120GB", price: "$70", data: "120GB Mobile Data", note: "5G Access • 1000GB data bank • Calls to 15 countries*", popular: false },
        { name: "180GB", price: "$85", data: "180GB Mobile Data", note: "5G Access • 1000GB data bank • Calls to 15 countries*", popular: false },
      ],
      share: [
        { name: "10GB", price: "$35", data: "10GB Shared", note: "Month to month • Data Share", popular: false },
        { name: "30GB", price: "$45", data: "30GB Shared", note: "4G Access • Free to 15 countries*", popular: true },
        { name: "45GB", price: "$60", data: "45GB Shared", note: "5G Access • Free to 15 countries*", popular: false },
        { name: "60GB", price: "$75", data: "60GB Shared", note: "5G Access • Free to 15 countries*", popular: false },
      ],
    },
    data: {
      main: [
        { name: "15GB", price: "$35", data: "15GB Data", note: "5G Access • 1000GB data bank", popular: false },
        { name: "45GB", price: "$40", data: "45GB Data", note: "5G Access • 1000GB data bank", popular: true },
        { name: "65GB", price: "$45", data: "65GB Data", note: "5G Access • 1000GB data bank", popular: false },
        { name: "100GB", price: "$55", data: "100GB Data", note: "5G Access • 1000GB data bank", popular: false },
        { name: "120GB", price: "$65", data: "120GB Data", note: "5G Access • 1000GB data bank", popular: false },
        { name: "180GB", price: "$80", data: "180GB Data", note: "5G Access • 1000GB data bank", popular: false },
      ],
      share: [
        { name: "10GB", price: "$25", data: "10GB Shared", note: "Month to month • Data Share", popular: false },
        { name: "30GB", price: "$40", data: "30GB Shared", note: "Most Popular • Data Share", popular: true },
        { name: "45GB", price: "$50", data: "45GB Shared", note: "5G Access • Data Share", popular: false },
        { name: "60GB", price: "$65", data: "60GB Shared", note: "5G Access • Data Share", popular: false },
      ],
    },
  };

  const getCIS = () => {
    if (category === "voice" && variant === "main")
      return "/docs/Jortel Mobile Critical Information Summary.pdf";
    if (category === "voice" && variant === "share")
      return "/docs/Jortel Mobile Voice Share Plans Critical Information Summary.pdf";
    if (category === "data" && variant === "main")
      return "/docs/Jortel Mobile Data Critical Information Summary.pdf";
    if (category === "data" && variant === "share")
      return "/docs/Jortel Mobile Data Sharing Critical Information Summary.pdf";
    return "";
  };

  const activePlans = planData[category][variant];

  return (
    <motion.main className="page plans-page" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <motion.div className="plans-hero-banner">
        <h1>J-Mobile Voice & Data</h1>
        <p>Stay connected with data banking, sharing, and mobile plan flexibility.</p>
      </motion.div>

      <section className="mobile-coverage-summary" aria-label="Mobile coverage information">
        <p>
          The mobile product of Jortel uses the Telstra Wholesale Mobile Network, with predicted coverage areas in all Australian major cities, most outer-metro areas and larger regional centres, as well as many smaller towns and transport corridors.
        </p>
        <Link to="/mobile-coverage">View coverage map and outage links</Link>
      </section>
      <p className="mobile-network-disclaimer">
        5G access is available on eligible plans in selected coverage areas with a compatible device. Actual coverage and performance may vary by location, device, network conditions and other factors.
      </p>

      {/* HEADER */}
      <div className="plans-header">
        <h1 className="plan-title">
          {category === "voice" ? <FaSimCard className="plan-icon" /> : <FaWifi className="plan-icon" />}
          {category === "voice" ? "J-Mobile Voice" : "J-Mobile Data"}
        </h1>

        <div className="toggle-row">
          <button className={`toggle ${category === "voice" ? "active" : ""}`} onClick={() => setCategory("voice")}>Voice</button>
          <button className={`toggle ${category === "data" ? "active" : ""}`} onClick={() => setCategory("data")}>Data</button>
        </div>

        <div className="toggle-row">
          <button className={`toggle ${variant === "main" ? "active" : ""}`} onClick={() => setVariant("main")}>Banking</button>
          <button className={`toggle ${variant === "share" ? "active" : ""}`} onClick={() => setVariant("share")}>Sharing</button>
        </div>
      </div>

      {/* GRID */}
      <AnimatePresence mode="wait">
        <motion.div className="plans-grid" key={`${category}-${variant}`}>
          {activePlans.map((p, i) => (
            <motion.div className={`plan-card ${p.popular ? "popular" : ""}`} key={i} whileHover={{ scale: 1.03 }}>
              {p.popular && <div className="badge">⭐ Most Popular</div>}
              <h3>{p.name}</h3>

              <div className="price">
                {p.price}<span className="per">/month</span>
              </div>

              <div className="data">{p.data}</div>
              <div className="note">{p.note}</div>

              <div className="cis-inline">
                <a href={getCIS()} target="_blank" rel="noopener noreferrer">
                  Critical Information Summary
                </a>
              </div>
            </motion.div>
          ))}

          {/* 🌍 INTERNATIONAL ROAMING TILE */}
          <motion.div className="plan-card roaming-tile" whileHover={{ scale: 1.03 }}>
            <FaGlobe className="roaming-icon" />
            <h3>International Roaming</h3>
            <div className="data">Roaming packs • Tips • 68 Countries</div>
            <div className="note">Activation instructions included</div>
            <Link to="/plans/mobile/roam" className="btn-roaming">Learn More</Link>
          </motion.div>

        </motion.div>
      </AnimatePresence>

      {category === "voice" && (
        <section className="watch-esim-banner" aria-label="Watch eSIM companion plan">
          <div>
            <span>Watch companion eSIM</span>
            <h2>Need an eSIM for your watch?</h2>
            <p>Add mobile connectivity to a compatible Apple Watch or Samsung Galaxy Watch for $10/month as a separate companion plan.</p>
          </div>
          <Link to="/plans/mobile/watch-esim">Learn how to activate</Link>
        </section>
      )}

    </motion.main>
  );
}
